import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Sparkles,
  Film,
  User,
  Store,
  Armchair,
  X,
  Download,
  RefreshCw,
  Loader2,
  AlertCircle,
  Clock,
  Play,
  Trash2,
  Zap,
  Gem,
  Type,
  Flame,
  CheckCircle2,
  ImagePlus,
} from 'lucide-react';
import { supabase } from '../supabaseClient';

/**
 * VIDEO STUDIO — AI social video generation (Veo 3.1 "Ingredients to Video")
 * ==========================================================================
 * The user drops in up to 3 reference photos — the PERSON who'll be on
 * camera, the STORE location, and the PRODUCT — plus a
 * plain-English description. We upload the photos to Supabase Storage,
 * POST to /api/generate-video (which starts a Veo long-running job), then
 * poll until the finished MP4 lands in the marketing-videos bucket.
 *
 * Generation takes 1–6 minutes. Cost per 8s video (Google, mid-2026):
 * Fast tier ≈ $1.20, Best tier ≈ $3.20 — keep that in mind for plan limits.
 *
 * No html2canvas / PNG capture here — completely separate pipeline from
 * FlyerBuilder, which is why this is its own component.
 */

export const SLOT_DEFS = [
  {
    role: 'person',
    label: 'Person',
    hint: 'Who’s on camera — clear face photo',
    icon: User,
  },
  {
    role: 'location',
    label: 'Your store',
    hint: 'Photo or walkthrough video of your store',
    icon: Store,
  },
  {
    role: 'product',
    label: 'Product',
    hint: 'The product to feature',
    icon: Armchair,
  },
];

const TIERS = [
  { id: 'fast', label: 'Fast', sublabel: '~1–2 min', icon: Zap },
  { id: 'quality', label: 'Best quality', sublabel: '~3–6 min', icon: Gem },
];

// Longer durations are chained Veo generations under the hood (8s each),
// stitched into one clip. 16s composes a fresh, independently QC'd
// keyframe per scene — a real scene cut, not one shot padded out — when a
// location photo is attached; otherwise it falls back to continuing the
// same shot (still one continuous, locked-identity clip). Capped at 2
// scenes (16s) rather than 3 (24s): field-tested — a 3rd independent Veo
// generation was where face fidelity started visibly slipping even with
// the keyframe system engaged, so 2 is the sweet spot for now.
// 'graphics' is a different mechanism entirely: Veo only ever generates
// ONE talking-head scene (so there's exactly one face AND one voice to
// stay consistent), everything after the first --- becomes a narrated
// graphic card instead of another Veo call — length is however long the
// narration takes, not a fixed chunk.
const DURATIONS = [
  // PROMO: the host talks to camera (reference-mode take, real face) and
  // the product appears in cutaways cut from its REAL photo — the edit
  // happens server-side after the take lands. Needs a person + a product.
  { id: 'promo', label: 'Promo', sublabel: 'You + product cutaways' },
  { id: 8, label: '8s', sublabel: '1 take' },
  // 16 = the backend's plan id. It is produced as ONE continuous take: an
  // 8s generation extended by 7s (15s real), the same host/room/voice
  // carried as pixels, not re-composed per scene. Falls back to two
  // stitched 8s shots only if Google refuses the extension.
  // 16 with a person + product = HOOK + PRODUCT: 8s of you talking, then 8s
  // of your exact product in your store with the offer on screen. Without a
  // product photo it stays one long take (extension).
  { id: 16, label: '16s', sublabel: 'You, then the product' },
  { id: 'graphics', label: 'Hook + Graphics', sublabel: 'Dynamic length' },
  // FOOTAGE MODE: nothing is generated at all — the user's OWN video is
  // the material. The AI transcribes it, picks the strongest moments,
  // cuts out dead air and filler, reframes vertical and captions it.
  { id: 'footage', label: 'Edit My Video', sublabel: 'Upload your own' },
];

// Up to this many source files per edit, matching MAX_FOOTAGE_SOURCES in
// api/generate-video.py.
export const MAX_FOOTAGE_FILES = 3;
// Client-side ceiling per file. The real limit is whatever the Supabase
// bucket allows (50MB by default unless raised) — this catches the
// hopeless cases early with a message that says what to do about it.
export const MAX_FOOTAGE_MB = 200;

export const processFootageFile = (file) => {
  if (!file.type.startsWith('video/')) {
    throw new Error(`${file.name}: upload a video file (MP4 or MOV)`);
  }
  if (file.size > MAX_FOOTAGE_MB * 1024 * 1024) {
    throw new Error(`${file.name} is ${Math.round(file.size / (1024 * 1024))}MB — keep uploads under ${MAX_FOOTAGE_MB}MB`);
  }
  return { file, name: file.name, sizeMb: Math.max(1, Math.round(file.size / (1024 * 1024))) };
};

// How long the finished cut should run. 15-60s is the band short-form
// actually performs in; the editor is told to come in UNDER the target
// rather than pad to it.
export const FOOTAGE_LENGTHS = [15, 30, 45, 60];

// Rotating status lines while Veo renders — keeps the wait feeling alive.
// Footage mode does genuinely different work from a Veo render, and
// "Casting your video…" while it's transcribing an upload reads as a
// stalled spinner. Same rotation, honest labels.
export const FOOTAGE_PROGRESS_LINES = [
  'Listening to your footage…',
  'Reading the transcript…',
  'Finding the strongest moments…',
  'Cutting out the dead air…',
  'Reframing and captioning…',
  'Finishing your edit…',
];

export const PROGRESS_LINES = [
  'Casting your video…',
  'Setting up the shot…',
  'Directing the scene…',
  'Recording dialogue and music…',
  'Color grading…',
  'Final cut in progress…',
];

const POLL_INTERVAL_MS = 8000;
const MAX_POLLS = 90; // ~12 minutes

// Downscale a File to a JPEG ≤ maxDim on the long edge. Phone photos are
// 3–8MB; Veo only needs identity/detail, and small refs keep the server-side
// fetch + base64 payload well inside Google's request limits.
const resizeImageFile = (file, maxDim = 1280) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Image processing failed'))),
        'image/jpeg',
        0.85
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });

// Grab a representative frame from an uploaded VIDEO (store walkthroughs,
// product pans) as a JPEG blob — all client-side, the video itself never
// uploads. Veo's ingredients mode takes images, so a good frame of the real
// showroom is how "film your store, we inject the host" works. Seeks ~25%
// in (capped at 1s) to skip the tap-to-record shake and any black lead-in.
// muted + playsInline are required for iOS Safari to decode without a tap.
const extractVideoFrame = (file, maxDim = 1280) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    const cleanup = () => URL.revokeObjectURL(url);

    video.onloadedmetadata = () => {
      const dur = Number.isFinite(video.duration) ? video.duration : 2;
      video.currentTime = Math.min(1.0, dur * 0.25);
    };
    video.onseeked = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) {
          cleanup();
          return reject(new Error('Could not read a frame from that video'));
        }
        const scale = Math.min(1, maxDim / Math.max(video.videoWidth, video.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        cleanup();
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('Frame capture failed'))),
          'image/jpeg',
          0.85
        );
      } catch (e) {
        cleanup();
        reject(new Error('Frame capture failed'));
      }
    };
    video.onerror = () => {
      cleanup();
      reject(new Error('Could not read that video — try a different clip or a photo'));
    };
    video.src = url;
  });

// Validate + normalize one ingredient (photo or video) into the slot shape
// { blob, preview, fromVideo, name }. Shared by VideoStudio and the Studio
// home composer so both entry points behave identically. Throws with a
// user-facing message on bad input.
export const processIngredientFile = async (file) => {
  const isVideo = file.type.startsWith('video/');
  const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!isVideo && !isImage) {
    throw new Error(`${file.name}: use a photo (JPG/PNG/WebP) or a video clip (MP4/MOV)`);
  }
  const blob = isVideo ? await extractVideoFrame(file) : await resizeImageFile(file);
  return { blob, preview: URL.createObjectURL(blob), name: file.name, fromVideo: isVideo };
};

// A pre-made design asset (a logo, a branded template) for Graphics mode's
// 'graphic' role — used EXACTLY as uploaded, no compositing. Deliberately
// skips resizeImageFile's canvas round-trip: that re-encodes to JPEG at
// 85%, which would flatten a transparent PNG logo to an unpredictable
// (often black) background in the BROWSER before the file ever reaches
// the backend's _flatten_transparency — which needs the real alpha
// channel to do that properly, onto a controlled white background.
export const processGraphicFile = (file) => {
  const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error(`${file.name}: use a PNG, JPG, or WebP image`);
  }
  if (file.size > 8 * 1024 * 1024) {
    throw new Error(`${file.name}: keep graphics under 8MB`);
  }
  return { blob: file, preview: URL.createObjectURL(file), name: file.name, mime: file.type };
};

// The three ingredient tiles (Person / Your store / Product) — ONE component
// rendered in two places: the Studio home composer (compact) and the video
// builder. Owns its hidden file inputs; emits the raw File via onFileSelected.
// Up to two extra angles of the SAME product — they feed the Product DNA
// extractor and the keyframe compositor (which accept 3 angles), killing
// invented backs and phantom cushions at the source. Veo itself still gets
// its 3-reference slice; extras are for the lock pipeline.
export const EXTRA_PRODUCT_KEYS = ['product_2', 'product_3'];
// Up to two more photos of the SAME host. Identity is the thing AI video
// gets wrong most; three angles of the real face give the keyframe
// compositor (up to 5 character refs) and Veo's reference-identity
// treatment far more to hold onto than one selfie. Sent as role 'person'.
export const EXTRA_PERSON_KEYS = ['person_2', 'person_3'];
// Which extra-slot keys hang under which main tile, plus the tiny caption.
const EXTRA_SLOTS = {
  product: { keys: EXTRA_PRODUCT_KEYS, caption: 'angles', title: 'Add another angle of the same product' },
  person: { keys: EXTRA_PERSON_KEYS, caption: 'more of you', title: 'Add another photo of the same person (different angle or expression)' },
};

export const IngredientSlots = ({ slots, onFileSelected, onClear, onPickSaved, saved, disabled = false, compact = false }) => {
  const inputRefs = useRef({});
  return (
    <div className="grid grid-cols-3 gap-3">
      {SLOT_DEFS.map(({ role, label, hint, icon: Icon }) => {
        const filled = slots?.[role];
        return (
          <div key={role} className="flex flex-col">
            <input
              ref={(el) => { inputRefs.current[role] = el; }}
              type="file"
              accept="image/jpeg,image/png,image/webp,video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (inputRefs.current[role]) inputRefs.current[role].value = '';
                if (file) onFileSelected(role, file);
              }}
            />
            {filled ? (
              <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-blue-300 group">
                <img src={filled.preview} alt={label} className="w-full h-full object-cover" />
                <button
                  onClick={() => onClear(role)}
                  className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                  <span className="text-[11px] font-bold text-white">{label}{filled.reused ? ' · saved' : filled.fromVideo ? ' · frame' : ''}</span>
                </div>
              </div>
            ) : (
              <button
                onClick={() => inputRefs.current[role]?.click()}
                disabled={disabled}
                className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 transition-all flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-blue-600 disabled:opacity-50"
              >
                <Icon className={compact ? 'w-5 h-5' : 'w-6 h-6'} />
                <span className={compact ? 'text-[10px] font-bold' : 'text-[11px] font-bold'}>{label}</span>
              </button>
            )}
            <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-slate-400 mt-1.5 leading-tight text-center px-0.5`}>{hint}</span>
            {/* Extra photos — more ANGLES of the same product, or more
                photos of the same HOST — shown once the main photo is in.
                Product extras feed DNA + the compositor; host extras feed
                the compositor, the graders, and Veo's identity references. */}
            {EXTRA_SLOTS[role] && filled && (
              <div className="flex gap-1.5 mt-1.5 justify-center">
                {EXTRA_SLOTS[role].keys.map((k) => {
                  const extra = slots?.[k];
                  return (
                    <div key={k} className="relative">
                      <input
                        ref={(el) => { inputRefs.current[k] = el; }}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (inputRefs.current[k]) inputRefs.current[k].value = '';
                          if (f) onFileSelected(k, f);
                        }}
                      />
                      {extra ? (
                        <div className="w-9 h-9 rounded-md overflow-hidden border border-blue-300 relative">
                          <img src={extra.preview} alt="angle" className="w-full h-full object-cover" />
                          <button
                            onClick={() => onClear(k)}
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center touch-manipulation"
                            title="Remove angle"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => inputRefs.current[k]?.click()}
                          disabled={disabled}
                          title={EXTRA_SLOTS[role].title}
                          className="w-9 h-9 rounded-md border border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50/50 text-slate-400 hover:text-blue-600 flex items-center justify-center text-sm font-bold disabled:opacity-50 touch-manipulation"
                        >
                          +
                        </button>
                      )}
                    </div>
                  );
                })}
                <span className="self-center text-[9px] text-slate-400 leading-tight">{EXTRA_SLOTS[role].caption}</span>
              </div>
            )}
            {/* Reuse row: past uploads for this role — tap to fill, no re-upload */}
            {!filled && onPickSaved && (saved?.[role]?.length || 0) > 0 && (
              <div className="flex gap-1 mt-1.5 justify-center flex-wrap">
                {saved[role].slice(0, 4).map((url) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => onPickSaved(role, url)}
                    disabled={disabled}
                    title="Reuse this photo"
                    className="w-8 h-8 rounded-md overflow-hidden border border-slate-200 hover:border-blue-400 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    <img src={url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==========================================================
// CAPTIONS HEALTH BANNER — runs the ?diag=1 check automatically when the
// Studio opens and, ONLY if captions are blocked, says exactly what's
// missing in plain English right on screen. No console, no Vercel logs.
// Renders nothing when healthy or when the backend predates the diag route.
// ==========================================================
const HEALTH_FIXES = {
  pillow: 'add  Pillow>=10.1  to requirements.txt and redeploy',
  ffmpeg: 'add  imageio-ffmpeg>=0.4.9  to requirements.txt and redeploy',
  caption_phrases: 'run:  alter table public.marketing_videos add column if not exists caption_phrases jsonb;',
};

export const CaptionsHealthBanner = () => {
  const [problems, setProblems] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const resp = await fetch('/api/generate-video?diag=1', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return; // older backend — stay silent
        const d = await resp.json();
        if (cancelled || !d) return;
        if (typeof d.verdict === 'string' && d.verdict.includes('BLOCKED')) {
          const found = [];
          if (d.pillow === false) found.push(HEALTH_FIXES.pillow);
          if (d.ffmpeg === false) found.push(HEALTH_FIXES.ffmpeg);
          if (d.db_columns && d.db_columns.caption_phrases === false) found.push(HEALTH_FIXES.caption_phrases);
          if (found.length) setProblems(found);
        }
        // STUCK-RENDER VERDICT: any generating row past 12 min gets an amber
        // card quoting what Google ACTUALLY said when we probed it just now.
        const reap = d.stuck_after_minutes || 15;
        const old = (d.generating_rows || []).filter((r) => (r.age_minutes || 0) > 12);
        if (old.length) {
          setStuck(old.map((r) => (
            `${r.tier || 'video'} render, ${Math.round(r.age_minutes)} min — Google says: ${r.state}. `
            + `Auto-fails at ${reap} min and refunds the slot.`
          )));
        }
      } catch (e) { /* diagnostics must never break the studio */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (dismissed || (!problems && !stuck)) return null;
  if (!problems && stuck) {
    return (
      <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 relative">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 w-5 h-5 rounded-full text-amber-300 hover:text-amber-500 flex items-center justify-center touch-manipulation"
          title="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <p className="text-xs font-bold text-amber-700 pr-6">A render looks stuck</p>
        <ul className="mt-1.5 space-y-1">
          {stuck.map((s) => (
            <li key={s} className="text-[11px] text-amber-700 leading-snug">• {s}</li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 relative">
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 w-5 h-5 rounded-full text-red-300 hover:text-red-500 flex items-center justify-center touch-manipulation"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <p className="text-xs font-bold text-red-700 pr-6">
        Captions can&rsquo;t render on this deployment
      </p>
      <ul className="mt-1.5 space-y-1">
        {problems.map((p) => (
          <li key={p} className="text-[11px] text-red-600 leading-snug font-mono">&bull; {p}</li>
        ))}
      </ul>
      <p className="mt-1.5 text-[10px] text-red-400">
        Videos still generate &mdash; they just ship without captions until this is fixed.
      </p>
    </div>
  );
};

// Style ids must stay in sync with VIDEO_STYLES in generate-video.py.
export const VIDEO_STYLE_DEFS = [
  { id: 'clean', label: 'Clean' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'energetic', label: 'Energetic' },
  { id: 'luxury', label: 'Luxury' },
  { id: 'bold', label: 'Bold' },
];

// Style pills + captions toggle — ONE component rendered on both the Studio
// home ingredients card and the builder view, so the "ultra modern" controls
// can never drift between surfaces.
export const VideoStyleControls = ({ style, setStyle, captions, setCaptions, effects, setEffects, viralHook, setViralHook, disabled = false }) => (
  <div className="mt-3">
    <div className="flex flex-wrap gap-1.5">
      {VIDEO_STYLE_DEFS.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => setStyle(id)}
          disabled={disabled}
          className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-all disabled:opacity-50 ${
            style === id
              ? 'border-blue-500 bg-blue-50 text-blue-900'
              : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
          }`}
        >
          {label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setCaptions(!captions)}
        disabled={disabled}
        title="TikTok pack: bold captions + punch cuts + flash transitions, burned in after render — deterministic on every video"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-all disabled:opacity-50 ${
          captions
            ? 'border-blue-500 bg-blue-50 text-blue-900'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <Type className="w-3 h-3" />
        Captions {captions ? 'on' : 'off'}
      </button>
      <button
        type="button"
        onClick={() => setEffects(!effects)}
        disabled={disabled}
        title="Zoom punches, light sweeps, flash transitions on the product"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-all disabled:opacity-50 ${
          effects
            ? 'border-blue-500 bg-blue-50 text-blue-900'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <Sparkles className="w-3 h-3" />
        Special FX
      </button>
      <button
        type="button"
        onClick={() => setViralHook(!viralHook)}
        disabled={disabled}
        title="Opens with a pattern-interrupt that stops the scroll, then cuts straight to the pitch"
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-all disabled:opacity-50 ${
          viralHook
            ? 'border-orange-500 bg-orange-50 text-orange-900'
            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
        }`}
      >
        <Flame className="w-3 h-3" />
        Viral hook
      </button>
    </div>
    {captions && (
      <p className="text-[10px] text-slate-400 mt-1.5">
        TikTok pack: bold pop-in captions + punch cuts + flash transitions at each beat, burned in after render. The words come from your description ("25% OFF", "THIS WEEKEND").
      </p>
    )}
  </div>
);

// ============================================================
// RecentVideosStrip — past generations, self-contained (fetch + delete +
// play). Rendered on the Studio HOME (so history never disappears when
// generation happens on screen one) and in the builder view.
// ============================================================
export const RecentVideosStrip = ({ storeId, onPlay, refreshToken = 0, embedded = false, notify = null }) => {
  const [videos, setVideos] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const resp = await fetch(`/api/generate-video?store_id=${encodeURIComponent(storeId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      setVideos(Array.isArray(data.videos) ? data.videos : []);
    } catch (e) {
      console.warn('Recent videos load skipped:', e);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // SELF-HEALING FOR STUCK RENDERS. Finalize work (download from Google,
  // captions, verify, upload) runs on the SERVER — but only when something
  // polls GET ?video_id=. Close the tab mid-render and the row sits
  // 'generating' forever: the classic "GIF loops, video never lands".
  // While any listed video is still generating, nudge each one every 10s;
  // the server finishes the job (or fails it honestly) and we refresh the
  // list the moment a status flips. This also un-parks rate-limit-queued
  // rows, whose retries are poll-driven too.
  useEffect(() => {
    // Backpressure: fresh rows (<30s) are the hero hook's job; hidden tabs
    // don't poll at all; cadence is 15s. Three pending rows at the old 10s
    // cadence was enough status-check volume to trip Google's per-minute
    // limits on the operations endpoint — which reads as an eternal loop.
    const pending = videos.filter((v) => {
      if (v.status !== 'generating') return false;
      const ageSec = (Date.now() - new Date(v.created_at || 0).getTime()) / 1000;
      return !(ageSec > 0 && ageSec < 30);
    });
    if (pending.length === 0) return undefined;
    let cancelled = false;

    const nudge = async () => {
      try {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token || cancelled) return;
        let flipped = false;
        for (const v of pending) {
          if (cancelled) return;
          try {
            const resp = await fetch(`/api/generate-video?video_id=${encodeURIComponent(v.id)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) continue;
            const data = await resp.json();
            if (data.status && data.status !== 'generating') {
              flipped = true;
              // The strip is often the thing that FINALIZES a render (user
              // walked away mid-generation) — so the caption outcome must
              // announce here too, not just in the hero hook.
              const cap = data.captions;
              if (notify && data.status === 'ready' && cap && cap.requested && !cap.burned) {
                notify(`Video ready — captions were skipped: ${cap.skip_reason || 'unknown reason'}`, 'error');
              }
            }
          } catch (e) { /* transient — next tick retries */ }
        }
        if (flipped && !cancelled) load();
      } catch (e) { /* transient */ }
    };

    nudge();
    const timer = setInterval(nudge, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [videos, load]);

  // ---- DELETE WITHOUT DIALOGS ----------------------------------------
  // No window.confirm — tiles vanish INSTANTLY and an Undo bar appears for
  // 5 seconds. The actual DELETE calls only fire after the window passes
  // (Gmail-style delayed commit), so Undo is a pure state restore with zero
  // backend involvement. Starting a new delete while one batch is pending
  // commits the previous batch first — one batch in flight keeps it simple.
  const pendingRef = useRef(null); // { items: [video], timer }
  const [undoCount, setUndoCount] = useState(0);

  const commitBatch = useCallback(async (batch) => {
    if (!batch) return;
    clearTimeout(batch.timer);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      for (const v of batch.items) {
        try {
          await fetch(`/api/generate-video?id=${encodeURIComponent(v.id)}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (e) { /* row survives; next load() re-syncs */ }
      }
    } catch (e) { /* ignore — list self-heals on next load */ }
  }, []);

  const scheduleDelete = useCallback((items) => {
    if (!items.length) return;
    // Flush any batch already waiting so batches never interleave.
    if (pendingRef.current) {
      const prev = pendingRef.current;
      pendingRef.current = null;
      commitBatch(prev);
    }
    setVideos((vs) => vs.filter((v) => !items.some((d) => d.id === v.id)));
    const batch = { items };
    batch.timer = setTimeout(() => {
      if (pendingRef.current === batch) pendingRef.current = null;
      setUndoCount(0);
      commitBatch(batch);
    }, 5000);
    pendingRef.current = batch;
    setUndoCount(items.length);
  }, [commitBatch]);

  const undoDelete = useCallback(() => {
    const batch = pendingRef.current;
    if (!batch) return;
    pendingRef.current = null;
    clearTimeout(batch.timer);
    setUndoCount(0);
    setVideos((vs) => {
      const merged = [...vs, ...batch.items];
      merged.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      return merged;
    });
  }, []);

  // Leaving the page commits the pending batch immediately — the user chose
  // delete and walked away; that's a commit, not an undo.
  useEffect(() => () => {
    const batch = pendingRef.current;
    pendingRef.current = null;
    if (batch) commitBatch(batch);
  }, [commitBatch]);

  if (videos.length === 0 && undoCount === 0) return null;

  // Compact "how old" label so you can tell generations apart at a glance.
  const ageLabel = (createdAt) => {
    if (!createdAt) return null;
    const ms = Date.now() - new Date(createdAt).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 60_000) return 'now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h`;
    return `${Math.floor(ms / 86_400_000)}d`;
  };

  const row = (
    <div className="flex gap-3 overflow-x-auto pb-1">
        {videos.map((v) => (
          <div key={v.id} className="flex-shrink-0 w-24 group relative">
            <button
              onClick={() => {
                if (v.status === 'ready' && v.video_url && onPlay) onPlay(v.video_url);
              }}
              className={`w-24 h-40 rounded-xl border overflow-hidden relative flex items-center justify-center touch-manipulation active:scale-[0.97] transition-transform ${
                v.status === 'ready'
                  ? 'border-slate-200 hover:border-blue-400 bg-slate-900'
                  : 'border-slate-200 bg-slate-100'
              }`}
            >
              {v.status === 'ready' && v.video_url ? (
                <>
                  {/* #t=0.1 media fragment makes Safari (and Chrome) actually
                      decode + paint a real frame with preload="metadata" —
                      without it, iOS shows a black box. No server-side poster
                      needed. */}
                  <video
                    src={`${v.video_url}#t=0.1`}
                    className="w-full h-full object-cover pointer-events-none"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  {/* Play affordance: always visible on touch, hover-reveal on lg */}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                    <span className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center">
                      <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                    </span>
                  </span>
                  {ageLabel(v.created_at) && (
                    <span className="absolute bottom-1 right-1 px-1 py-px rounded bg-black/55 text-white text-[9px] font-semibold leading-none">
                      {ageLabel(v.created_at)}
                    </span>
                  )}
                </>
              ) : v.status === 'generating' ? (
                <span className="flex flex-col items-center gap-1 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-[10px] font-semibold">Rendering</span>
                </span>
              ) : (
                <span className="flex flex-col items-center gap-1 text-red-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-[10px] font-semibold">Failed</span>
                </span>
              )}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); scheduleDelete([v]); }}
              className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-slate-200 shadow-sm text-slate-400 hover:text-red-500 flex lg:hidden lg:group-hover:flex items-center justify-center touch-manipulation"
              title="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </button>
            <p className="text-[10px] text-slate-500 mt-1 truncate" title={v.description}>{v.description}</p>
          </div>
        ))}
    </div>
  );

  // embedded: bare row for use inside a parent section (the Studio library);
  // default: self-carded with its own title (the builder view).
  const failed = videos.filter((v) => v.status === 'failed');

  // Slim action strip: Undo bar while a delete batch is pending; otherwise a
  // one-tap "Clear N failed" when there are dead tiles to sweep. All inline
  // UI — zero browser dialogs anywhere in this flow.
  const actionBar = (undoCount > 0 || failed.length > 0) ? (
    <div className="flex items-center justify-between mb-2 min-h-[24px]">
      {undoCount > 0 ? (
        <>
          <span className="text-xs text-slate-500">
            Deleted {undoCount} video{undoCount > 1 ? 's' : ''}
          </span>
          <button
            onClick={undoDelete}
            className="text-xs font-bold text-blue-600 hover:text-blue-700 px-2 py-0.5 rounded-md hover:bg-blue-50 touch-manipulation"
          >
            Undo
          </button>
        </>
      ) : (
        <>
          <span />
          <button
            onClick={() => scheduleDelete(failed)}
            className="text-xs font-semibold text-slate-400 hover:text-red-500 px-2 py-0.5 rounded-md hover:bg-red-50 touch-manipulation"
          >
            Clear {failed.length} failed
          </button>
        </>
      )}
    </div>
  ) : null;

  if (embedded) return <div>{actionBar}{row}</div>;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <h3 className="text-sm font-bold text-slate-900 mb-3">Recent videos</h3>
      {actionBar}
      {row}
    </div>
  );
};

// ============================================================
// useSavedIngredients — the reusable ingredient library. Every generation
// already persists its reference URLs (with roles) on the marketing_videos
// row, so the library builds ITSELF: no save button, no schema change.
// Returns { person: [url...], location: [...], product: [...] }, newest
// first, deduped, capped at 8 per role.
// ============================================================
export const useSavedIngredients = (storeId, refreshToken = 0) => {
  const [saved, setSaved] = useState({ person: [], location: [], product: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const resp = await fetch(`/api/generate-video?store_id=${encodeURIComponent(storeId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        const byRole = { person: [], location: [], product: [] };
        (Array.isArray(data.videos) ? data.videos : []).forEach((v) => {
          (Array.isArray(v.image_urls) ? v.image_urls : []).forEach((img) => {
            const role = (img?.role || '').toLowerCase();
            if (byRole[role] && img.url && !byRole[role].includes(img.url) && byRole[role].length < 8) {
              byRole[role].push(img.url);
            }
          });
        });
        if (!cancelled) setSaved(byRole);
      } catch (e) {
        console.warn('Saved ingredients load skipped:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [storeId, refreshToken]);

  return saved;
};

// ============================================================
// useVideoGeneration — the whole engine (upload refs -> start Veo job ->
// poll -> re-hosted MP4) as a hook, shared by the Studio HOME screen (so
// generation runs right on the first screen, phone mockup as the player)
// and by the VideoStudio builder view. One engine, two surfaces.
// ============================================================
export const useVideoGeneration = ({ storeId, addNotification, onFinished, vertical = 'generic' }) => {
  // phase: 'compose' | 'generating' | 'ready' | 'failed'
  const [phase, setPhase] = useState('compose');
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [progressIdx, setProgressIdx] = useState(0);
  // true while the run is parked behind Google's rate limit — the backend
  // auto-starts it when a slot frees; the UI just tells the truth meanwhile.
  const [queued, setQueued] = useState(false);

  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  // Did THIS run ask for captions? Lets the ready handler distinguish
  // "burn skipped" from "plan never saved" (missing migration).
  const wantedCaptionsRef = useRef(false);
  // A poll that triggers server-side work (queued retry, download+burn on
  // finalize) can outlive the 8s interval — never let ticks stack.
  const pollBusyRef = useRef(false);
  // Chained (>8s) runs need proportionally more poll budget — set by
  // generate() right before startPolling, read fresh on every tick.
  const pollBudgetRef = useRef(MAX_POLLS);

  const stopTimers = () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
  };

  useEffect(() => () => stopTimers(), []);

  // Upload one ingredient blob to the existing `flyers` bucket
  // (same pattern as FlyerBuilder.uploadImageToStorage).
  const uploadSlotToStorage = async (role, blob) => {
    const filename = `video_ref_${storeId}_${Date.now()}_${role}.jpg`;
    const { error: uploadError } = await supabase.storage
      .from('flyers')
      .upload(filename, blob, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
        upsert: false,
      });
    if (uploadError) {
      console.error(`${role} upload failed:`, uploadError);
      return null;
    }
    const { data: urlData } = supabase.storage.from('flyers').getPublicUrl(filename);
    return urlData?.publicUrl || null;
  };

  // Source footage goes up as-is — no canvas round-trip, no re-encode. The
  // backend needs the original file: it transcribes its audio and cuts
  // from its actual frames, and anything we did to it here would be a
  // generation loss applied before the edit even starts.
  const uploadFootageToStorage = async (file, index) => {
    const ext = (file.name.split('.').pop() || 'mp4').toLowerCase().slice(0, 4);
    const filename = `footage_${storeId}_${Date.now()}_${index}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('flyers')
      .upload(filename, file, {
        contentType: file.type || 'video/mp4',
        cacheControl: '3600',
        upsert: false,
      });
    if (uploadError) {
      console.error('footage upload failed:', uploadError);
      // The most common real failure is the bucket's own size limit, and
      // "row too large"-style errors tell the user nothing actionable.
      throw new Error(
        `Could not upload ${file.name}: ${uploadError.message || 'upload failed'}. `
        + 'If it is a long recording, try trimming it or exporting at 1080p.'
      );
    }
    const { data: urlData } = supabase.storage.from('flyers').getPublicUrl(filename);
    return urlData?.publicUrl || null;
  };

  // Custom graphics keep their real mime/extension (unlike uploadSlotToStorage's
  // hardcoded .jpg) — a PNG logo's transparency has to survive the upload
  // for the backend's _flatten_transparency to have real alpha to work with.
  const uploadGraphicToStorage = async (blob, index) => {
    const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
    const filename = `graphic_${storeId}_${Date.now()}_${index}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('flyers')
      .upload(filename, blob, {
        contentType: blob.type || 'image/png',
        cacheControl: '3600',
        upsert: false,
      });
    if (uploadError) {
      console.error('graphic upload failed:', uploadError);
      return null;
    }
    const { data: urlData } = supabase.storage.from('flyers').getPublicUrl(filename);
    return urlData?.publicUrl || null;
  };

  const startPolling = (videoId, token) => {
    let polls = 0;

    setElapsed(0);
    elapsedTimerRef.current = setInterval(() => {
      setElapsed((s) => s + 1);
    }, 1000);

    pollTimerRef.current = setInterval(async () => {
      if (pollBusyRef.current) return;
      pollBusyRef.current = true;
      polls += 1;
      setProgressIdx((i) => (i + 1) % PROGRESS_LINES.length);

      if (polls > pollBudgetRef.current) {
        stopTimers();
        setPhase('failed');
        setError('This is taking longer than expected. It may still finish — check back in Recent videos in a couple of minutes.');
        if (onFinished) onFinished();
        return;
      }

      try {
        const resp = await fetch(`/api/generate-video?video_id=${encodeURIComponent(videoId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) return; // transient — keep polling
        const data = await resp.json();

        setQueued(Boolean(data.queued));

        if (data.status === 'ready' && data.video_url) {
          stopTimers();
          setVideoUrl(data.video_url);
          setPhase('ready');
          if (onFinished) onFinished();
          // Captions are self-announcing now: the ready payload carries a
          // report. Skipped burn → toast the exact reason (usually a missing
          // dependency); plan never saved → the migration warning.
          const cap = data.captions;
          if (addNotification) {
            if (cap && cap.requested && !cap.burned) {
              addNotification(`Video ready — captions were skipped: ${cap.skip_reason || 'unknown reason'}`, 'error');
            } else if (wantedCaptionsRef.current && cap && !cap.requested) {
              addNotification('Captions never saved to this video — run the caption_phrases migration in Supabase.', 'error');
            } else {
              addNotification('Your video is ready! 🎬', 'success');
            }
          }
        } else if (data.status === 'failed') {
          stopTimers();
          setPhase('failed');
          setError(data.error || 'Generation failed — try adjusting your photos or description.');
          if (onFinished) onFinished();
        }
        // 'generating' -> keep going
      } catch (e) {
        console.warn('Poll hiccup (retrying):', e);
      } finally {
        pollBusyRef.current = false;
      }
    }, POLL_INTERVAL_MS);
  };

  // FOOTAGE EDIT — upload the user's own clips, then hand the backend the
  // URLs. Same async contract as a generation (video_id + polling), so the
  // player, recents strip and error handling below are shared verbatim.
  const runFootageEdit = async ({ footageFiles, description, footageSeconds }) => {
    if (!footageFiles?.length) {
      const msg = 'Add at least one video to edit';
      setError(msg);
      return { ok: false, error: msg };
    }
    setError('');
    setPhase('generating');
    setProgressIdx(0);
    setQueued(false);
    wantedCaptionsRef.current = true;   // footage edits are always captioned

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Please log in again');

      const urls = [];
      for (let i = 0; i < footageFiles.length; i++) {
        const item = footageFiles[i];
        const url = item.url || await uploadFootageToStorage(item.file, i);
        if (!url) throw new Error(`Could not upload ${item.name}`);
        urls.push(url);
      }

      const resp = await fetch('/api/generate-video', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          footage_mode: true,
          store_id: storeId,
          footage: urls,
          description,              // optional angle — what the cut should serve
          target_seconds: footageSeconds,
          aspect_ratio: '9:16',
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.video_id) {
        throw new Error(data.error || `Video service error (${resp.status})`);
      }
      // Transcribing and re-encoding real footage runs longer than a Veo
      // render and spans several poll legs — give it room.
      pollBudgetRef.current = MAX_POLLS * 4;
      startPolling(data.video_id, token);
      return { ok: true };
    } catch (err) {
      console.error('Footage edit failed:', err);
      stopTimers();
      setPhase('compose');
      const msg = err.message || 'Could not edit that footage';
      setError(msg);
      if (addNotification) addNotification(msg, 'error');
      return { ok: false, error: msg };
    }
  };

  // Kick off a generation. Returns { ok, error? } so callers on surfaces
  // without an inline error banner (the Studio home) can toast the message.
  const generate = async ({ description, slots, tier = 'fast', businessName, style = 'clean', captions = false, effects = false, viralHook = false, duration = 8, customGraphics = [], autoScript = true, adSeconds = 24, footageFiles = [], footageSeconds = 30 }) => {
    const desc = (description || '').trim();

    // FOOTAGE MODE takes an entirely different route: the user's files are
    // the material, so there's no description requirement, no reference
    // photos, and nothing generated. Handled before every check below,
    // all of which are about generation inputs it doesn't have.
    if (duration === 'footage') {
      return runFootageEdit({ footageFiles, description: desc, footageSeconds });
    }
    const filledRoles = [...SLOT_DEFS.map((s) => s.role), ...EXTRA_PRODUCT_KEYS, ...EXTRA_PERSON_KEYS]
      .filter((r) => slots?.[r]);

    if (!desc) {
      const msg = 'Describe the video you want — the deal, the vibe, what to say';
      setError(msg);
      return { ok: false, error: msg };
    }
    // Graphics mode is the one case that can run with ZERO photos — a
    // pure script-and-voiceover video, every scene a text card. Every
    // other mode still needs at least one reference photo to animate.
    if (filledRoles.length === 0 && customGraphics.length === 0 && duration !== 'graphics') {
      const msg = 'Add at least one photo — a person, your store, or a product';
      setError(msg);
      return { ok: false, error: msg };
    }
    if (duration === 'promo' && !(slots?.person && slots?.product)) {
      const msg = 'Promo needs a photo of you (Person) and a photo of the product';
      setError(msg);
      return { ok: false, error: msg };
    }

    setError('');
    setPhase('generating');
    setProgressIdx(0);
    setQueued(false);
    wantedCaptionsRef.current = Boolean(captions);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Please log in again');

      const images = [];
      for (const role of filledRoles) {
        const slot = slots[role];
        // Reused ingredients already live in storage — no re-upload.
        const url = slot.url ? slot.url : await uploadSlotToStorage(role, slot.blob);
        if (!url) throw new Error(`Could not upload your ${role} photo — try again`);
        // Extra angles / extra host photos are pseudo-keys client-side;
        // the backend knows one role per kind.
        images.push({
          url,
          role: role.startsWith('product') ? 'product' : role.startsWith('person') ? 'person' : role,
        });
      }
      // CUSTOM GRAPHICS — pre-made assets (a logo, a branded template) used
      // EXACTLY as uploaded for a graphic scene, no AI compositing. Role
      // 'graphic' is never sent to Veo (see _top3_for_veo's explicit
      // exclusion) — it only ever reaches _build_graphics_scenes.
      for (let i = 0; i < customGraphics.length; i++) {
        const g = customGraphics[i];
        const url = g.url ? g.url : await uploadGraphicToStorage(g.blob, i);
        if (!url) throw new Error('Could not upload one of your graphics — try again');
        images.push({ url, role: 'graphic' });
      }

      const resp = await fetch('/api/generate-video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_id: storeId,
          business_name: businessName,
          description: desc,
          model_tier: tier,
          video_style: style,
          captions,
          effects,
          viral_hook: viralHook,
          // SCRIPT MODE: the description IS the video, spoken verbatim — no
          // injected CTA, no forced product-showcase beats, no business-name
          // opener. Veo films exactly what's written instead of treating the
          // text as a "brief" it's free to embellish. The backend strips the
          // map card in script mode (an address burn-in doesn't fit a script
          // the user fully controls), so it's intentionally not sent here.
          script_mode: true,
          // GRAPHICS MODE: Veo only ever generates the first scene (the
          // hook) — everything after the first --- becomes a narrated
          // graphic card instead of another Veo call. duration_seconds
          // stays 8 (that's the hook's length); the backend figures out
          // the real total once it knows how long each card's narration
          // actually runs.
          ...(duration === 'graphics' ? { graphics_mode: true } : {}),
          // PROMO MODE: reference-mode host take + product b-roll from the
          // real photo + the server-side edit. One 8s take.
          ...(duration === 'promo' ? { promo_mode: true } : {}),
          // AD DIRECTOR: with auto_script on, the description is treated as
          // an IDEA and the backend writes the actual ad — hook, problem,
          // solution, proof, CTA — instead of narrating the sentence you
          // typed. Off means your text is spoken verbatim, the original
          // behavior. Only meaningful in graphics mode.
          ...(duration === 'graphics' ? { auto_script: autoScript, ad_seconds: adSeconds } : {}),
          vertical,
          aspect_ratio: '9:16',
          duration_seconds: (duration === 'graphics' || duration === 'promo') ? 8 : duration,
          images,
        }),
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.video_id) {
        throw new Error(data.error || `Video service error (${resp.status})`);
      }

      setQueued(Boolean(data.queued));
      // Chained (>8s) runs are N Veo generations back-to-back — scale the
      // client poll budget with them or a 24s run gets cut off mid-render.
      // Graphics mode is one Veo op (the hook) plus finalize-leg work —
      // same wait profile as a normal 8s video, no extra polling needed.
      // Graphics mode is one Veo op at most, but each scene now designs a
      // real 2K frame (image call + QC) and the backend builds them across
      // SEVERAL poll legs — so it needs a longer client budget than an 8s
      // video, not the same one.
      const chainMultiplier = (duration === 'graphics' || duration === 'promo') ? 3 : Math.round(duration / 8);
      pollBudgetRef.current = MAX_POLLS * Math.max(1, chainMultiplier);
      startPolling(data.video_id, token);
      return { ok: true };
    } catch (err) {
      console.error('Generate video failed:', err);
      stopTimers();
      setPhase('compose');
      const msg = err.message || 'Something went wrong — try again';
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const reset = () => {
    stopTimers();
    setVideoUrl('');
    setPhase('compose');
    setError('');
    setQueued(false);
  };

  // Load an already-finished video (e.g. from the Recent strip) into the player.
  // playToken bumps on EVERY call — keying the <video> on `${url}::${token}`
  // means tapping the SAME video twice still remounts (visible restart),
  // instead of silently no-oping because the key didn't change.
  const [playToken, setPlayToken] = useState(0);
  const showVideo = (url) => {
    stopTimers();
    wantedCaptionsRef.current = false;
    setVideoUrl(url);
    setPhase('ready');
    setError('');
    setPlayToken((t) => t + 1);
  };

  return { phase, videoUrl, error, setError, elapsed, progressIdx, queued, playToken, generate, reset, showVideo };
};

const VideoStudio = ({ storeId, businessName, addNotification, vertical = 'generic', initialPrompt = '', initialSlots = null, initialTier = null, initialDuration = null, initialStyle = null, initialCaptions = null, initialEffects = null, initialViralHook = null, initialShowUrl = null, onShowConsumed, onPromptConsumed }) => {
  // slots: { person: {file, blob, preview}, location: {...}, product: {...} }
  const [slots, setSlots] = useState({});
  // Pre-made graphics (logos, branded templates) for Graphics mode — used
  // exactly as uploaded, no AI compositing. An array, not a slot: any
  // number of scenes can each want their own.
  const [customGraphics, setCustomGraphics] = useState([]);
  const [description, setDescription] = useState('');
  const [tier, setTier] = useState('fast');
  const [duration, setDuration] = useState(8);
  const [style, setStyle] = useState('clean');
  const [captions, setCaptions] = useState(false);
  const [effects, setEffects] = useState(false);
  const [viralHook, setViralHook] = useState(false);
  // AD DIRECTOR — on by default: the box is an IDEA and the AI writes the
  // ad from it (hook / problem / solution / proof / CTA). Turn it off to
  // have your words narrated exactly as typed, which is what this mode
  // used to do unconditionally.
  const [autoScript, setAutoScript] = useState(true);
  // FOOTAGE MODE — the user's own uploaded clips, and how long the
  // finished cut should run.
  const [footageFiles, setFootageFiles] = useState([]);
  const [footageSeconds, setFootageSeconds] = useState(30);
  // Recents live in the shared RecentVideosStrip; this token tells it to
  // refetch whenever a generation on this surface finishes.
  const [recentsToken, setRecentsToken] = useState(0);

  // The shared engine — same hook the Studio home screen uses. Destructured
  // names (phase/videoUrl/error/elapsed/progressIdx) match the old local
  // state on purpose so the render code below needs zero changes.
  const {
    phase, videoUrl, error, setError, elapsed, progressIdx, queued, playToken, generate, reset, showVideo,
  } = useVideoGeneration({ storeId, addNotification, vertical, onFinished: () => setRecentsToken((t) => t + 1) });

  // On mobile the player is order-first at the TOP of the page while the
  // Recent strip lives at the BOTTOM — a tap down there swaps the player
  // completely off-screen and looks like nothing happened. Scroll it into
  // view on every user-initiated play/generate (lg+ has it side-by-side).
  const previewRef = useRef(null);
  const graphicFileInputRef = useRef(null);
  const footageFileInputRef = useRef(null);
  const scrollPreviewIntoView = () => {
    const wide = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(min-width: 1024px)').matches;
    if (wide) return;
    requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  // Reusable ingredients — refreshes whenever a generation finishes.
  const savedIngredients = useSavedIngredients(storeId, recentsToken);
  const handlePickSaved = (role, url) => {
    setSlots((prev) => ({ ...prev, [role]: { url, preview: url, reused: true } }));
    setError('');
  };

  // ------------------------------------------------------------
  // Seed from the Studio home composer. The home screen collects the whole
  // flow — description AND ingredient photos — so when both arrive we fire
  // the generation immediately, exactly like FlyerBuilder's auto-generate.
  // Prompt-only handoff just prefills and waits (a video needs photos and
  // costs real money per run). Overrides dodge the state-commit race.
  // ------------------------------------------------------------
  // Mobile home-screen library taps land here with a URL to play — the
  // picker's right-pane player doesn't exist below lg. The consume→null
  // protocol (parent clears initialShowUrl via onShowConsumed) already
  // prevents re-fire loops, so there's deliberately NO one-shot ref guard:
  // a guard made the SECOND handoff dead whenever this component stayed
  // mounted, which is exactly the "opens once, never again" bug.
  useEffect(() => {
    if (!initialShowUrl) return;
    showVideo(initialShowUrl);
    scrollPreviewIntoView();
    if (onShowConsumed) onShowConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialShowUrl]);

  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    const seed = (initialPrompt || '').trim();
    const seedSlots = initialSlots && Object.keys(initialSlots).length > 0 ? initialSlots : null;
    if (!seed && !seedSlots) return;
    seededRef.current = true;
    if (seed) setDescription(seed);
    if (seedSlots) setSlots(seedSlots);
    if (initialTier) setTier(initialTier);
    if (initialDuration) setDuration(initialDuration);
    if (initialStyle) setStyle(initialStyle);
    if (initialCaptions !== null && initialCaptions !== undefined) setCaptions(Boolean(initialCaptions));
    if (initialEffects !== null && initialEffects !== undefined) setEffects(Boolean(initialEffects));
    if (initialViralHook !== null && initialViralHook !== undefined) setViralHook(Boolean(initialViralHook));
    if (onPromptConsumed) onPromptConsumed();
    if (seed && seedSlots) handleGenerate({
      description: seed,
      slots: seedSlots,
      tier: initialTier || tier,
      duration: initialDuration || duration,
      style: initialStyle || style,
      captions: (initialCaptions !== null && initialCaptions !== undefined) ? Boolean(initialCaptions) : captions,
      effects: (initialEffects !== null && initialEffects !== undefined) ? Boolean(initialEffects) : effects,
      viralHook: (initialViralHook !== null && initialViralHook !== undefined) ? Boolean(initialViralHook) : viralHook,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, initialSlots]);

  // ------------------------------------------------------------
  // Slot handlers
  // ------------------------------------------------------------
  const handleSlotFile = async (role, file) => {
    try {
      const processed = await processIngredientFile(file);
      setSlots((prev) => ({ ...prev, [role]: processed }));
      setError('');
    } catch (err) {
      setError(err.message || 'Could not process that file');
    }
  };

  const clearSlot = (role) => {
    setSlots((prev) => {
      const next = { ...prev };
      if (next[role]?.preview?.startsWith('blob:')) URL.revokeObjectURL(next[role].preview);
      delete next[role];
      return next;
    });
  };

  const GRAPHICS_MAX_SCENES = 6; // mirrors the backend's GRAPHICS_MAX_SCENES cap

  const handleCustomGraphicFile = (file) => {
    try {
      const processed = processGraphicFile(file);
      setCustomGraphics((prev) => prev.length >= GRAPHICS_MAX_SCENES ? prev : [...prev, processed]);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not add that graphic');
    }
  };

  const clearCustomGraphic = (index) => {
    setCustomGraphics((prev) => {
      const item = prev[index];
      if (item?.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleFootageFile = (file) => {
    try {
      const processed = processFootageFile(file);
      setFootageFiles((prev) => prev.length >= MAX_FOOTAGE_FILES ? prev : [...prev, processed]);
      setError('');
    } catch (err) {
      setError(err.message || 'Could not add that video');
    }
  };

  const clearFootageFile = (index) => {
    setFootageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ------------------------------------------------------------
  // Generate
  // ------------------------------------------------------------
  const handleGenerate = async (overrides = {}) => {
    // Auto-start from the Studio home passes {description, slots, tier}
    // directly so we don't depend on React having committed seeded state.
    // The manual button passes a SyntheticEvent — guard so only a plain
    // options object counts as overrides (FlyerBuilder pattern).
    const opts = (overrides && !overrides.nativeEvent && typeof overrides === 'object') ? overrides : {};
    scrollPreviewIntoView();
    await generate({
      description: opts.description ?? description,
      slots: opts.slots ?? slots,
      tier: opts.tier ?? tier,
      duration: opts.duration ?? duration,
      style: opts.style ?? style,
      captions: opts.captions ?? captions,
      effects: opts.effects ?? effects,
      viralHook: opts.viralHook ?? viralHook,
      customGraphics: opts.customGraphics ?? customGraphics,
      autoScript: opts.autoScript ?? autoScript,
      footageFiles: opts.footageFiles ?? footageFiles,
      footageSeconds: opts.footageSeconds ?? footageSeconds,
      businessName,
    });
  };

  const handleStartOver = () => {
    reset();
  };

  const fmtElapsed = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="h-full min-h-0 flex flex-col lg:flex-row gap-4 overflow-y-auto lg:overflow-visible">

      {/* ================= LEFT — COMPOSER ================= */}
      <div className="flex-shrink-0 lg:flex-1 min-w-0 flex flex-col gap-4 lg:overflow-y-auto pb-2">

        <CaptionsHealthBanner />

        {/* Reference photo slots — irrelevant in footage mode, where the
            user's own uploaded video IS the material. */}
        <div className={`bg-white border border-slate-200 rounded-2xl p-4 ${duration === 'footage' ? 'hidden' : ''}`}>
          <div className="flex items-center gap-2 mb-1">
            <Film className="w-4 h-4 text-blue-600" />
            <h3 className="text-sm font-bold text-slate-900">Your ingredients</h3>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            Add up to 3 photos — or drop a video and we’ll pull the best frame. The AI keeps each one recognizable in the final video.
          </p>

          <IngredientSlots
            slots={slots}
            onFileSelected={handleSlotFile}
            onClear={clearSlot}
            onPickSaved={handlePickSaved}
            saved={savedIngredients}
            disabled={phase === 'generating'}
          />
        </div>

        {/* Description */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <h3 className="text-sm font-bold text-slate-900 mb-1">What’s the video about?</h3>
          <p className="text-xs text-slate-500 mb-3">
            The deal, the product’s best feature, the vibe — the AI writes the script, dialogue, and music from this.
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 2000))}
            disabled={phase === 'generating'}
            placeholder={`e.g. "Labor Day sale — this sectional is 40% off this weekend only. High energy, make people want to come in today."`}
            rows={4}
            className="w-full text-sm border border-slate-200 rounded-xl px-3.5 py-3 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none placeholder:text-slate-400 disabled:bg-slate-50"
          />
        </div>

        {/* Quality tier + generate */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex gap-2 flex-1">
              {TIERS.map(({ id, label, sublabel, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setTier(id)}
                  disabled={phase === 'generating'}
                  className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all disabled:opacity-50 ${
                    tier === id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${tier === id ? 'text-blue-600' : 'text-slate-400'}`} />
                  <span className="text-left">
                    <span className={`block text-xs font-bold ${tier === id ? 'text-blue-900' : 'text-slate-700'}`}>{label}</span>
                    <span className="block text-[10px] text-slate-400">{sublabel}</span>
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={handleGenerate}
              disabled={phase === 'generating'}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white text-sm font-bold shadow-sm transition-all disabled:opacity-60"
            >
              {phase === 'generating'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Sparkles className="w-4 h-4" />}
              {phase === 'generating' ? 'Creating…' : 'Generate video'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {DURATIONS.map(({ id, label, sublabel }) => (
              <button
                key={id}
                onClick={() => setDuration(id)}
                disabled={phase === 'generating'}
                className={`flex-1 min-w-[120px] flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 transition-all disabled:opacity-50 ${
                  duration === id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <Clock className={`w-4 h-4 flex-shrink-0 ${duration === id ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="text-left">
                  <span className={`block text-xs font-bold ${duration === id ? 'text-blue-900' : 'text-slate-700'}`}>{label}</span>
                  <span className="block text-[10px] text-slate-400">{sublabel}</span>
                </span>
              </button>
            ))}
          </div>
          {duration === 'promo' && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              A real promo edit: you talk to camera the whole take (filmed from your photos, so
              your face holds), and the product appears in cutaways cut from its actual photo
              while your voice keeps going. Add 2–3 photos of yourself and 2–3 angles of the
              product; a store photo stages the product in your space.
            </p>
          )}
          {duration === 16 && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              Two scenes. First 8s: you talking to camera (from your photos). Second 8s: no
              person — your exact product in your store with music, and the rest of your text
              on screen. Put a line with just <code className="font-mono">---</code> where the
              switch goes; what you say goes above it, the offer goes below it. Needs a photo of
              you and of the product. Without a product photo it stays one long take.
            </p>
          )}
          {duration === 'footage' && (
            <>
              <p className="text-[10px] text-slate-400 mt-1.5">
                Upload what you actually shot — a talking take, a walkthrough, a few phone clips.
                The AI listens to it, picks the strongest moments, cuts out the dead air and the
                “ums”, reframes it vertical around whoever is talking, and captions every word.
                No photos or script needed.
              </p>

              <div className="mt-2 rounded-xl border-2 border-dashed border-slate-200 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <Film className="w-4 h-4 flex-shrink-0 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-700">Your footage</p>
                    <p className="text-[10px] text-slate-400">
                      Up to {MAX_FOOTAGE_FILES} clips, {MAX_FOOTAGE_MB}MB each. MP4 or MOV, any orientation.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => footageFileInputRef.current?.click()}
                    disabled={footageFiles.length >= MAX_FOOTAGE_FILES || phase === 'generating'}
                    className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-blue-600 border border-blue-200 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    Add video
                  </button>
                  <input
                    ref={footageFileInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFootageFile(file);
                      e.target.value = '';
                    }}
                  />
                </div>

                {footageFiles.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {footageFiles.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                        <Film className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
                        <span className="flex-1 min-w-0 truncate text-[11px] font-medium text-slate-700">{f.name}</span>
                        <span className="flex-shrink-0 text-[10px] text-slate-400">{f.sizeMb}MB</span>
                        <button
                          type="button"
                          onClick={() => clearFootageFile(i)}
                          disabled={phase === 'generating'}
                          className="flex-shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  How long should the cut be?
                </p>
                <div className="flex gap-1.5">
                  {FOOTAGE_LENGTHS.map((secs) => (
                    <button
                      key={secs}
                      type="button"
                      onClick={() => setFootageSeconds(secs)}
                      disabled={phase === 'generating'}
                      className={`flex-1 px-2 py-2 rounded-xl border-2 text-xs font-bold transition-colors disabled:opacity-50 ${
                        footageSeconds === secs
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-slate-200 hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      {secs}s
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">
                  A target, not a quota — the edit ends when the point lands, even if that’s shorter.
                  Use the box above to say what the video is for (“get people booking deliveries”) and
                  the cut will favour the moments that serve it.
                </p>
              </div>
            </>
          )}

          {duration === 'graphics' && (
            <>
              <button
                type="button"
                onClick={() => setAutoScript((v) => !v)}
                className={`mt-2 w-full flex items-start gap-2.5 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                  autoScript ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Sparkles className={`w-4 h-4 mt-0.5 flex-shrink-0 ${autoScript ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="flex-1 min-w-0">
                  <span className={`block text-xs font-bold ${autoScript ? 'text-blue-900' : 'text-slate-700'}`}>
                    Write the ad for me
                  </span>
                  <span className="block text-[10px] text-slate-500 mt-0.5">
                    {autoScript
                      ? 'Just describe your idea — the AI writes the real ad (hook, problem, solution, proof, call to action) and designs a premium graphic for every scene.'
                      : 'Your words are narrated exactly as typed. Separate scenes with a line containing just --- and put spoken lines in quotes.'}
                  </span>
                </span>
                <span className={`flex-shrink-0 mt-0.5 w-9 h-5 rounded-full transition-colors ${autoScript ? 'bg-blue-600' : 'bg-slate-300'}`}>
                  <span className={`block w-4 h-4 mt-0.5 rounded-full bg-white transition-transform ${autoScript ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
              </button>
              <p className="text-[10px] text-slate-400 mt-1.5">
                {autoScript
                  ? 'Add a person photo and the AI films your opening line to camera, then cuts to designed graphic scenes. No person photo? Every scene is a designed graphic — no camera, no character, just narration over premium visuals. It never invents statistics, reviews, or guarantees: any number in your ad has to come from what you wrote.'
                  : 'Add a person photo and the AI films your first line as the hook (add --- and write more lines for graphic scenes after it). Put what\u2019s spoken in quotes ("...") and the rest is the scene direction, e.g. Show the pricing page. "Plans start at $29 a month."'}
                {' Add screenshots of your product to the Product photo slot and those scenes show one composited into a realistic device mockup.'}
              </p>
              {/* CUSTOM GRAPHICS — pre-made design assets used exactly as
                  uploaded (no AI compositing), one per graphic scene in
                  order. The AI still generates a scene automatically
                  (device mockup from a Product screenshot, or a text card)
                  for any scene beyond how many you've supplied here. */}
              <div className="mt-2 rounded-xl border-2 border-dashed border-slate-200 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <ImagePlus className="w-4 h-4 flex-shrink-0 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-700">Use your own graphics</p>
                    <p className="text-[10px] text-slate-400">
                      Upload a logo or branded design and it's used exactly as-is for a graphic
                      scene — no AI changes. One per scene, in order.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => graphicFileInputRef.current?.click()}
                    disabled={customGraphics.length >= GRAPHICS_MAX_SCENES}
                    className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-blue-600 border border-blue-200 hover:bg-blue-50 disabled:opacity-50 transition-colors"
                  >
                    Add
                  </button>
                  <input
                    ref={graphicFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleCustomGraphicFile(file);
                      e.target.value = '';
                    }}
                  />
                </div>
                {customGraphics.length > 0 && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {customGraphics.map((g, i) => (
                      <div key={i} className="relative w-12 h-12 rounded-md overflow-hidden border border-blue-300">
                        <img src={g.preview} alt={`graphic ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => clearCustomGraphic(i)}
                          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black/70 text-white flex items-center justify-center touch-manipulation"
                          title="Remove"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                        <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[8px] font-bold text-center leading-tight">
                          {i + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          <VideoStyleControls
            style={style}
            setStyle={setStyle}
            captions={captions}
            setCaptions={setCaptions}
            effects={effects}
            setEffects={setEffects}
            viralHook={viralHook}
            setViralHook={setViralHook}
            disabled={phase === 'generating'}
          />

          {error && (
            <div className="mt-3 flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Recent videos — shared strip, also shown on the Studio home */}
        <RecentVideosStrip
          storeId={storeId}
          onPlay={(url) => { showVideo(url); scrollPreviewIntoView(); }}
          refreshToken={recentsToken}
          notify={addNotification}
        />
      </div>

      {/* ================= RIGHT — PREVIEW ================= */}
      {/* Hidden entirely on mobile while composing — the placeholder phone is
          pure decoration and cost ~500px of scroll. scroll-mt keeps the
          scrollIntoView target clear of sticky chrome above. */}
      <div
        ref={previewRef}
        className={`${phase === 'compose' ? 'hidden lg:flex' : 'flex'} lg:w-[340px] flex-shrink-0 items-start justify-center scroll-mt-4 ${phase !== 'compose' ? 'order-first lg:order-none' : ''}`}
      >
        <div className="w-full max-w-[280px] mx-auto">

          {/* MOBILE generating/failed: compact status cards instead of the
              ~500px phone mockup, so the whole state fits above the fold —
              this is the fix for "the view is cut off while it's creating". */}
          {phase === 'generating' && (
            <div className="lg:hidden w-full rounded-2xl bg-slate-950 px-4 py-4 flex items-center gap-4 text-left">
              <div className="relative flex-shrink-0">
                <div className="w-11 h-11 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                <Film className="w-4 h-4 text-blue-400 absolute inset-0 m-auto" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">
                  {queued
                    ? 'Line’s full at Google — holding your spot'
                    : duration === 'footage'
                    ? FOOTAGE_PROGRESS_LINES[progressIdx % FOOTAGE_PROGRESS_LINES.length]
                    : PROGRESS_LINES[progressIdx]}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  {queued ? 'auto-retrying about once a minute' : `${fmtElapsed} · usually 1–4 min`}
                </p>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                  Keep working — it lands in Recent videos when done.
                </p>
              </div>
            </div>
          )}

          {phase === 'failed' && (
            <div className="lg:hidden w-full rounded-2xl bg-slate-950 px-4 py-4 flex items-start gap-3 text-left">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">That one didn’t make it</p>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{error}</p>
              </div>
            </div>
          )}

          {/* Phone-shaped frame, 9:16. For the transient states above it's
              lg-only; on mobile it appears only with an actual video, and its
              width is capped so the FULL frame fits in ~52% of the viewport
              height instead of running off the bottom of the screen. */}
          <div
            className={`relative w-full max-w-[min(100%,calc(52dvh*9/16))] lg:max-w-full mx-auto rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-xl overflow-hidden ${(phase === 'generating' || phase === 'failed') ? 'hidden lg:block' : ''}`}
            style={{ aspectRatio: '9/16' }}
          >

            {phase === 'compose' && (
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <Film className="w-10 h-10 text-blue-500" />
                <p className="text-sm font-bold text-blue-900">Your video shows here</p>
                <p className="text-xs text-blue-700/70 leading-relaxed">
                  8-second vertical ad with real spoken dialogue and music — ready for TikTok, Reels & Shorts
                </p>
              </div>
            )}

            {phase === 'generating' && (
              <div className="absolute inset-0 bg-slate-950 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
                  <Film className="w-5 h-5 text-blue-400 absolute inset-0 m-auto" />
                </div>
                <p className="text-sm font-bold text-white">
                  {queued
                    ? 'Line’s full at Google — holding your spot'
                    : duration === 'footage'
                    ? FOOTAGE_PROGRESS_LINES[progressIdx % FOOTAGE_PROGRESS_LINES.length]
                    : PROGRESS_LINES[progressIdx]}
                </p>
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{queued ? 'auto-retrying about once a minute' : `${fmtElapsed} · usually 1–4 min`}</span>
                </div>
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  You can keep working — this finishes in the background and lands in Recent videos.
                </p>
              </div>
            )}

            {phase === 'ready' && videoUrl && (
              <video
                key={`${videoUrl}::${playToken}`}
                src={videoUrl}
                controls
                playsInline
                loop
                className="absolute inset-0 w-full h-full object-cover bg-black"
              />
            )}

            {phase === 'failed' && (
              <div className="absolute inset-0 bg-slate-950 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <AlertCircle className="w-8 h-8 text-red-400" />
                <p className="text-sm font-bold text-white">That one didn’t make it</p>
                <p className="text-xs text-slate-400 leading-relaxed">{error}</p>
              </div>
            )}
          </div>

          {/* Actions under the phone */}
          {phase === 'ready' && videoUrl && (
            <div className="flex gap-2 mt-3">
              <a
                href={videoUrl}
                download={`${(businessName || 'video').replace(/\s+/g, '_')}_ad.mp4`}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download MP4
              </a>
              <button
                onClick={handleStartOver}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                New video
              </button>
            </div>
          )}

          {phase === 'failed' && (
            <button
              onClick={handleStartOver}
              className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Adjust & try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoStudio;